<template>
    <error-boundary>
        <section class="dealers-archive">
            <div id="dealers-app" class="dealers-app">
                <div class="dealers-app__header">
                    <h1 class="dealers-app__title">
                        Find a Dealer
                    </h1>
                </div>
                <div class="dealers-app__inputs">
                     <!-- Location request button -->
                      <div class="dealers-app__inputs--group dealers-app__inputs--location">
                         <button 
                            v-if="!userLocation && !userLocationloading" 
                            class="button dealers-app__inputs--location-button" 
                            type="button" 
                            @click="getLocation"
                            title="Use your current location to find nearby dealers"
                        >
                            <MapPin />
                            Use My Location
                        </button>
                        <!-- Location status indicator -->
                        <div v-if="userLocationloading" class="dealers-app__location-status">
                            <loader />
                            <span>Getting your location...</span>
                        </div>
                        <div v-if="userLocation" class="dealers-app__location-status dealers-app__location-status--success">
                            <MapPin />
                            <span>Using your location</span>
                        </div>
                        <div v-if="userLocationError" class="dealers-app__location-status dealers-app__location-status--error">
                            <span>{{ userLocationError }}</span>
                        </div>
                      </div>
                    <div class="dealers-app__inputs--group">
                        <div class="dealers-app__inputs--text-field">
                            <svg xmlns="http://www.w3.org/2000/svg" class="svg_icon bi-search" viewBox="0 0 16 16"><path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"></path></svg>
                            <input 
                                id="userPlace" 
                                class="input" 
                                type="text" 
                                placeholder="Enter City Name or Postal Code:"
                                v-model="searchObject.searchParams"
                                @keyup.enter="performSearch"
                            >
                        </div>
                    </div>
                    <button class="button dealers-app__inputs--search-button" type="button" @click="performSearch">
                        <Search />
                        Search
                    </button>
                    <!-- Select the radius for the search -->
                     <div class="dealers-app__inputs--radius">
                        <Radar />
                        <select id="radius" class="select dealers-app__inputs--radius-button" v-model="searchObject.radius">
                            <option value="0">Radius</option>
                            <option value="250">250 miles</option>
                            <option value="500">500 miles</option>
                        </select>
                     </div>
                    
                </div>
                <div ref="filtersContainer" class="dealers-app__filters">
                    <div class="dealers-app__filters--content">
                        <h3 class="dealers-app__filters--title">Filter by</h3>
                        
                        <!-- Show loading state for filter options -->
                        <div v-if="filterOptionsLoading" class="dealers-app__filters--loading">
                            <span>Loading filters...</span>
                        </div>
                        
                        <!-- Show error if filter options failed to load -->
                        <div v-if="filterOptionsError" class="dealers-app__filters--error">
                            <p>Error loading filters: {{ filterOptionsError }}</p>
                        </div>
                        
                        <!-- Show filter dropdowns when loaded -->
                        <div class="dealers-app__filters--dropdowns" v-if="!filterOptionsLoading && !filterOptionsError">
                            <filter-dropdown
                                v-if="productOptions.options.length > 0"
                                :options="productOptions.options"
                                :name="productOptions.filterName"
                                v-model="searchObject.printers"
                            ></filter-dropdown>
                            <filter-dropdown
                                v-if="inksOptions.options.length > 0"
                                :options="inksOptions.options"
                                :name="inksOptions.filterName"
                                v-model="searchObject.inks"
                            ></filter-dropdown>
                            
                            <!-- Show message if no filters are available -->
                            <div v-if="productOptions.options.length === 0 && inksOptions.options.length === 0" 
                                 class="dealers-app__filters--empty">
                                <p>No filter options configured. Please configure filters in the admin panel.</p>
                            </div>
                        </div>
                        
                        <div class="dealers-app__filters--group reset-filters">
                            <button class="dealers-app__filters--reset-button" type="button" @click="resetFilters">
                                <BrushCleaning />
                                Reset Filters
                            </button>
                        </div>
                    </div>
                </div>
                
                <!-- Loading state when no dealers have been loaded yet -->
                <div class="dealers-app__loading" v-if="loading && dealersLocations.length === 0 && !error">
                    <loader />
                </div>
                
                <!-- Error state -->
                <div class="dealers-app__error" v-if="error">
                    <p>Error loading dealers: {{ error }}</p>
                </div>
                
                <!-- Initial state message when no search context is available -->
                <div class="dealers-app__initial-state" v-if="!loading && !error && dealersLocations.length === 0 && !userLocation && !searchObject.searchParams">
                    <div class="dealers-app__initial-state--content">
                        <h3>Find Dealers Near You</h3>
                        <p>To get started, please:</p>
                        <ul>
                            <li>Use your current location by clicking "Use My Location"</li>
                            <li>Or enter a city name or postal code in the search field</li>
                        </ul>
                    </div>
                </div>
                
                <!-- No dealers found message when using radius search with user location -->
                <div class="dealers-app__no-dealers" v-if="!loading && !error && dealersLocations.length === 0 && searchObject.radius > 0 && userLocation && !isShowingOnlineRetailers && !isMixedSearch">
                    <div class="dealers-app__no-dealers--content">
                        <h3>No dealers found in your area</h3>
                        <p>We couldn't find any dealers within {{ searchObject.radius }} miles of your location. Try searching in a different city or postal code, or adjust your search filters.</p>
                    </div>
                </div>
                
                <!-- Dealers list and map -->
                <div ref="dealersListWrapper" v-if="groupedDealers.length > 0" class="dealers-app__list--wrapper">
                    <!-- Loading overlay when updating existing results -->
                    <div v-if="loading" class="dealers-app__loading-overlay">
                        <div class="dealers-app__loading-overlay--spinner">
                            <loader />
                        </div>
                    </div>

                    <div ref="dealersList" class="dealers-app__list">
                        <!-- Online retailers fallback message -->
                        <div class="dealers-app__online-retailers-notice" v-if="isShowingOnlineRetailers && groupedDealers.length > 0">
                            <div class="dealers-app__online-retailers-notice--content">
                                <h3>{{ fallbackTitle }}</h3>
                                <p>{{ fallbackMessage }}</p>
                            </div>
                        </div>
                        
                        <!-- Mixed search message -->
                        <div class="dealers-app__mixed-search-notice" v-if="isMixedSearch && groupedDealers.length > 0">
                            <div class="dealers-app__mixed-search-notice--content">
                                <h3>{{ fallbackTitle }}</h3>
                                <p>{{ mixedSearchMessage }}</p>
                                <div class="product-breakdown">
                                    <span class="local-products">🏪 Local: {{ localProducts.join(', ') }}</span>
                                    <span class="online-products">🌐 Online: {{ onlineProducts.join(', ') }}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="dealers-app__list--dealer-list">
                            <dealer-card
                                v-for="dealerGroup in groupedDealers"
                                :key="dealerGroup.dealerId"
                                :dealer-group="dealerGroup"
                                v-model="selectedDealer"
                            ></dealer-card>
                        </div>
                    </div>
                    <selected-dealer 
                        v-if="selectedDealer && !loading" 
                        :selected-dealer="selectedDealer" 
                        :filters-container="filtersContainer"
                    ></selected-dealer>
                </div>
            </div>
        </section>
    </error-boundary>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue'
import FilterDropdown from '../components/FilterDropdown.vue';
import DealerCard from '../components/DealerCard.vue';
import SelectedDealer from '../components/SelectedDealer.vue';
import ErrorBoundary from '../components/ErrorBoundary.vue';

import Loader from '../components/Loader.vue';
import { BrushCleaning, Search, Earth, Radar, MapPin } from 'lucide-vue-next';
import { useDealerLocations } from '../composables/useDealerLocations.js';
import { useDealerFallback } from '../composables/useDealerFallback.js';
import { useUserLocation } from '../composables/useUserLocation';
import { useGooglePlacesAutocomplete } from '../composables/useGooglePlacesAutocomplete.js';
import { useDealerGrouping } from '../composables/useDealerGrouping.js';
import { useFilterOptions } from '../composables/useFilterOptions.js';

const selectedDealer = ref(null);
const filtersContainer = ref(null);
const dealersListWrapper = ref(null);
const dealersList = ref(null);

// Fetch dealer locations
const { userLocation, userLocationError, userLocationloading, getLocation, initializeLocation } = useUserLocation();

// Initialize Google Places Autocomplete
const { selectedLocation, isLoading: autocompleteLoading, error: autocompleteError, clearSelection } = useGooglePlacesAutocomplete('userPlace');

const {
    dealersLocations,
    loading,
    error,
    fetchDealerLocations
} = useDealerLocations();

// Group dealers by dealerId
const { groupedDealers } = useDealerGrouping(dealersLocations);

// Fetch filter options dynamically
const { filterOptions, loading: filterOptionsLoading, error: filterOptionsError } = useFilterOptions();

// Initialize fallback functionality
const { 
    isShowingOnlineRetailers, 
    isMixedSearch,
    shouldTriggerFallback, 
    setFallbackState, 
    setMixedSearchState,
    clearFallbackState, 
    fallbackMessage,
    mixedSearchMessage, 
    fallbackTitle,
    localProducts,
    onlineProducts
} = useDealerFallback();

// Add default radius for user location-based searches
const DEFAULT_RADIUS = 500; // 500 miles default radius

const searchObject = ref({
    searchParams:'',
    radius: 0, // Start with 0 radius to show all dealers initially
    printers: [],
    inks: [],
})

// Computed properties for filter options
const productOptions = computed(() => ({
    filterName: 'Printers',
    options: filterOptions.value.printers || []
}));

const inksOptions = computed(() => ({
    filterName: 'Inks',
    options: filterOptions.value.inks || []
}));

// Add debounce mechanism to prevent rapid-fire updates
let watcherTimeout = null;

// Watch for autocomplete location selection
watch(selectedLocation, (newLocation, oldLocation) => {
    // Clear any existing timeout
    if (watcherTimeout) {
        clearTimeout(watcherTimeout);
    }
    
    // Debounce the watcher to prevent rapid-fire updates
    watcherTimeout = setTimeout(async () => {
        try {
            // Use nextTick to ensure we're outside of any Google Maps callback context
            await nextTick();
            
            if (newLocation && isInitialized && !loading.value) {
                // Validate location data thoroughly with multiple checks
                if (!newLocation || typeof newLocation !== 'object') {
                    //console.error('Invalid location object received:', newLocation);
                    return;
                }
                
                if (!newLocation.hasOwnProperty('lat') || !newLocation.hasOwnProperty('lng')) {
                    //console.error('Location object missing lat/lng properties:', newLocation);
                    return;
                }
                
                const lat = newLocation.lat;
                const lng = newLocation.lng;
                
                if (lat === null || lat === undefined || lng === null || lng === undefined || 
                    isNaN(lat) || isNaN(lng) || typeof lat !== 'number' || typeof lng !== 'number') {
                    //console.error('Invalid location coordinates received:', { lat, lng, typeof_lat: typeof lat, typeof_lng: typeof lng });
                    return;
                }
                
                // Validate coordinate ranges
                if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                    //console.error('Coordinates out of valid range:', { lat, lng });
                    return;
                }
                
                // Create a completely new object to avoid any reference issues
                const cleanLocation = {
                    lat: Number(lat),
                    lng: Number(lng)
                };
                
                // Double-check the cleaned coordinates
                if (isNaN(cleanLocation.lat) || isNaN(cleanLocation.lng)) {
                    //console.error('Failed to create clean coordinates:', cleanLocation);
                    return;
                }
                

                
                // Keep the place name in the search input for display purposes
                searchObject.value.searchParams = String(newLocation.formatted_address || '');
                
                // Set the selected location as user location for proximity search
                userLocation.value = cleanLocation;
                
                // Set default radius when user selects a location
                searchObject.value.radius = DEFAULT_RADIUS;
                

                
                // Trigger search when user selects a location from autocomplete
                await performSearch();
            }
        } catch (error) {
            console.error('Error in autocomplete location watcher:', error);
            // Don't propagate the error to prevent app crashes
            // Instead, show a user-friendly message
            if (error.message && !error.message.includes('Mn is not a function')) {
                console.warn('Setting generic error message for user');
            }
        }
    }, 100); // 100ms debounce
}, { flush: 'post' }); // Use post flush to ensure DOM updates are complete

// Search and reset functions
const performSearch = async () => {
    const searchProducts = [...searchObject.value.printers, ...searchObject.value.inks].filter(Boolean);
    const searchParams = {
        products: searchProducts,
    }
    
    // Determine if we have a valid search context
    const hasUserLocation = userLocation.value && userLocation.value.lat && userLocation.value.lng;
    const hasSearchParams = searchObject.value.searchParams && searchObject.value.searchParams.trim();
    const hasRadius = searchObject.value.radius > 0;
    const hasFilters = searchProducts.length > 0;
    
    // Don't perform search if we have no valid context
    if (!hasUserLocation && !hasSearchParams) {

        return;
    }
    
    // Clear fallback state at start of search
    clearFallbackState();
    
    // If we have coordinates (from autocomplete or user location) and a radius, use proximity search
    if (hasRadius && hasUserLocation) {
        searchParams.userLocation = userLocation.value
        searchParams.radius = searchObject.value.radius
        // Don't send text search when doing proximity search with coordinates
    } else if (hasSearchParams) {
        // Send text search if available (works with or without user location)
        searchParams.search = searchObject.value.searchParams
        if (hasUserLocation) {
            searchParams.userLocation = userLocation.value;
        }
    } else if (hasUserLocation && searchObject.value.radius === 0) {
        // If user location exists but no radius, show dealers near user location with default radius
        searchParams.userLocation = userLocation.value;
        searchParams.radius = DEFAULT_RADIUS;
    }
    

    
    // Enable mixed search for filtered searches with radius
    if (hasFilters && hasRadius) {
        searchParams.enableMixedSearch = true;
    }
    
    // Perform search (with mixed search capability)
    const searchResult = await fetchDealerLocations(searchParams);
    
    // Handle different search result types
    if (searchResult && searchResult.searchType === 'mixed') {
        // Mixed search: both local dealers and online retailers found

        setMixedSearchState(
            searchResult.localProducts || [],
            searchResult.onlineProducts || [],
            searchParams.radius || 0
        );
    } else if (searchResult && shouldTriggerFallback(searchResult.results, hasFilters, searchParams.radius || 0)) {
        // Standard fallback: no local dealers, try online retailers only

        
        const fallbackParams = {
            ...searchParams,
            enableFallback: true,
            enableMixedSearch: false
        };
        
        const fallbackResult = await fetchDealerLocations(fallbackParams);
        
        if (fallbackResult && fallbackResult.results.length > 0) {
            setFallbackState(searchProducts, searchParams.radius || 0);

        }
    }
    
    // set the selectedDealers as the first dealer in the list if available
    if (groupedDealers.value.length > 0) {
        selectedDealer.value = groupedDealers.value[0].primaryLocation;
    }
}

const resetFilters = async () => {
    searchObject.value = {
        searchParams: '',
        radius: userLocation.value ? DEFAULT_RADIUS : 0, // Set radius based on whether we have user location
        printers: [],
        inks: [],
    }
    
    // Clear fallback state when resetting
    clearFallbackState();
    
    // Clear autocomplete selection
    clearSelection();
    
    // Only fetch dealers if we have user location
    if (userLocation.value) {
        await fetchDealerLocations({
            userLocation: userLocation.value,
            radius: DEFAULT_RADIUS
        });
    } else {
        // Clear dealers when no location context exists
        dealersLocations.value = [];
        selectedDealer.value = null;
    }
}

const setupMapWidth = async () => { 
    // adjust dealerDetail width based on screen size
    try { 

        // set the width of the dealersList to be the equivalent of 34% of the filtersContainer width for screen sizes resolution above 1024px
        if (window.innerWidth > 1024) {

            if (filtersContainer.value && dealersListWrapper.value && dealersList.value) {
                const filtersWidth = filtersContainer.value.offsetWidth;
                const dealersListWidth = Math.max(filtersWidth * 0.34, 300); // Ensure minimum width of 300px
                dealersListWrapper.value.style.width = `${dealersListWidth}px`;
                dealersList.value.style.width = `${dealersListWidth}px`;
            }
        } else {

            // For smaller screens, set a fixed width
            if (dealersListWrapper.value && dealersList.value) {
                dealersListWrapper.value.style.width = '100%';
                dealersList.value.style.width = '100%';
            }
        }
        // Add event listener to adjust width on window resize
        window.addEventListener('resize', setupMapWidth);
    } catch (error) {
        console.error('Error setting up map width:', error);
    }
}

// Watch for filter changes (printers and inks) and auto-trigger search
watch([() => searchObject.value.printers, () => searchObject.value.inks], async () => {
    // Only trigger search if we're initialized, have some existing results or search context, and have valid search context
    const hasValidContext = userLocation.value || searchObject.value.searchParams;
    if (isInitialized && !loading.value && hasValidContext && (dealersLocations.value.length > 0 || searchObject.value.searchParams || (searchObject.value.radius > 0 && userLocation.value))) {
        await performSearch();
        setupMapWidth();
    }
}, { deep: true });

// Watch for radius changes and auto-trigger search if we have a location context
watch(() => searchObject.value.radius, async (newRadius, oldRadius) => {
    // Only trigger search if radius changed, we're initialized, and we have location context
    // Prevent triggering during initial setup
    if (isInitialized && newRadius !== oldRadius && newRadius > 0 && userLocation.value && !loading.value) {
        await performSearch();
        setupMapWidth();
    }
});

// Track initialization state to prevent redundant API calls
let isInitialized = false;
let hasUserLocationSet = false;

// Watch for user location availability - handles both fresh location and cached location
watch(userLocation, async (newUserLocation) => {
    if (newUserLocation && !hasUserLocationSet) {
        hasUserLocationSet = true;
        
        // Set default radius when user location becomes available
        searchObject.value.radius = DEFAULT_RADIUS;
        
        // If we're initialized and have user location, update dealers with location-based search
        if (isInitialized) {

            await fetchDealerLocations({
                userLocation: newUserLocation,
                radius: DEFAULT_RADIUS,
                search: searchObject.value.searchParams,
                products: [...searchObject.value.printers, ...searchObject.value.inks].filter(Boolean),
            });
            // set the selectedDealers as the first dealer in the list if available
            if (groupedDealers.value.length > 0) {
                selectedDealer.value = groupedDealers.value[0].primaryLocation;
            }
            setupMapWidth();
        } else {

            setupMapWidth();
        }
        
    }
}, { immediate: true });

onMounted(async () => {
    // Initialize location (check permissions and use cached location if available)
    await initializeLocation();
    
    // Only fetch dealers if we have user location
    if (userLocation.value) {
        const initialParams = {
            userLocation: userLocation.value,
            radius: DEFAULT_RADIUS
        };
        

        await fetchDealerLocations(initialParams);
        
        // Set the selectedDealer as the first dealer in the list if available
        if (groupedDealers.value.length > 0) {
            selectedDealer.value = groupedDealers.value[0].primaryLocation;
        }
    } else {

    }
    
    // Mark as initialized after initial load is complete
    isInitialized = true;
    setupMapWidth();
})

// Cleanup timeout on unmount
onUnmounted(() => {
    if (watcherTimeout) {
        clearTimeout(watcherTimeout);
        watcherTimeout = null;
    }
})
</script>

<style scoped>
.dealers-app__initial-state {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
    padding: 2rem;
    grid-column-start: 1;
    grid-column-end: 3;
}

.dealers-app__initial-state--content {
    text-align: center;
    max-width: 500px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.dealers-app__initial-state--content h3 {
    margin-bottom: 1rem;
    color: #333;
    font-size: 1.5rem;
}

.dealers-app__initial-state--content p {
    margin-bottom: 1rem;
    color: #666;
    line-height: 1.5;
}

.dealers-app__initial-state--content ul {
    text-align: left;
    color: #666;
    line-height: 1.5;
}

.dealers-app__initial-state--content li {
    margin-bottom: 0.5rem;
}

.dealers-app__no-dealers {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
    padding: 2rem;
}

.dealers-app__no-dealers--content {
    text-align: center;
    max-width: 500px;
    max-width: 500px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.dealers-app__no-dealers--content h3 {
    margin-bottom: 1rem;
    color: #333;
    font-size: 1.5rem;
}

.dealers-app__no-dealers--content p {
    margin-bottom: 1.5rem;
    color: #666;
    line-height: 1.5;
}

.dealers-app__online-retailers-notice {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem 2rem;
    margin-bottom: 1rem;
    background-color: #fff4ed;
    border: 1px solid #ffb380;
    border-radius: 8px;
}

.dealers-app__online-retailers-notice--content {
    text-align: right;
    max-width: 600px;
}

.dealers-app__online-retailers-notice--content h3 {
    margin-bottom: 0.5rem;
    color: #cc5500;
    font-size: 1.25rem;
}

.dealers-app__online-retailers-notice--content p {
    margin: 0;
    color: #b34d00;
    line-height: 1.5;
    font-size: 0.95rem;
}

.dealers-app__loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.9);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9;
    border-radius: 8px;
}

.dealers-app__loading-overlay--spinner {
    background-color: white;
    padding: 1.5rem;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    text-align: center;
}

.dealers-app__list--wrapper {
    position: relative;
}

/* Location-related styles */
.dealers-app__inputs--location-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background-color: #e0457b;
    color: white;
    border: none;
    padding: 8px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
    transition: background-color 0.2s ease;
}

.dealers-app__inputs--location-button:hover {
    background-color: #c03965;
}

.dealers-app__location-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    border-radius: 4px;
    font-size: 0.85rem;
    background-color: #f5f5f5;
    color: #666;
}

.dealers-app__location-status--success {
    background-color: #d4edda;
    color: #155724;
}

.dealers-app__location-status--error {
    background-color: #f8d7da;
    color: #721c24;
}

.dealers-app__location-status span {
    font-size: 0.85rem;
}

/* Filter-related styles */
.dealers-app__filters--loading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 4px;
    margin-bottom: 1rem;
}

.dealers-app__filters--loading span {
    font-size: 0.9rem;
    color: #6c757d;
}

.dealers-app__filters--error {
    padding: 1rem;
    background-color: #f8d7da;
    color: #721c24;
    border-radius: 4px;
    margin-bottom: 1rem;
}

.dealers-app__filters--error p {
    margin: 0;
    font-size: 0.9rem;
}

.dealers-app__filters--empty {
    padding: 1rem;
    background-color: #fff3cd;
    color: #856404;
    border-radius: 4px;
    margin-bottom: 1rem;
    text-align: center;
}

.dealers-app__filters--empty p {
    margin: 0;
    font-size: 0.9rem;
}

/* Online retailers notice styles */
.dealers-app__online-retailers-notice {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1.5rem;
    margin-bottom: 1rem;
    background-color: #fff0e6;
    border: 1px solid #ffb380;
    border-radius: 8px;
}

.dealers-app__online-retailers-notice--content {
    text-align: center;
    max-width: 600px;
}

.dealers-app__online-retailers-notice--content h3 {
    margin-bottom: 0.5rem;
    color: #ff9955;
    font-size: 1.25rem;
}

.dealers-app__online-retailers-notice--content p {
    margin: 0;
    color: #e6661a;
    line-height: 1.5;
}

/* Mixed search notice styles */
.dealers-app__mixed-search-notice {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1.5rem;
    margin-bottom: 1rem;
    background-color: #FFF3E0;
    border: 1px solid #FFCC02;
    border-radius: 8px;
}

.dealers-app__mixed-search-notice--content {
    text-align: center;
    max-width: 700px;
}

.dealers-app__mixed-search-notice--content h3 {
    margin-bottom: 0.5rem;
    color: #E65100;
    font-size: 1.25rem;
}

.dealers-app__mixed-search-notice--content p {
    margin: 0 0 1rem 0;
    color: #F57C00;
    line-height: 1.5;
}

.product-breakdown {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
}

.local-products,
.online-products {
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
}

.local-products {
    background-color: #E8F5E8;
    color: #2E7D32;
}

.online-products {
    background-color: #fff0e6;
    color: #ff9955;
}
</style>